import { apiFetch } from "./client";

export type Conversation = {
  _id: string;
  title: string;
  listingId?: string;
  participantIds: string[];
  lastMessageAt: string;
  archived?: boolean;
};

export type Message = {
  _id: string;
  conversationId: string;
  senderId?: string;
  senderName: string;
  senderRole: "eleve" | "tuteur" | "system";
  body: string;
  attachments?: Array<{
    fileName: string;
    mimeType: string;
    size: number;
    url: string;
  }>;
  createdAt: string;
};

export type RecentMessage = {
  conversationId: string;
  title: string;
  lastMessage: Message;
};

export async function fetchConversations(userId?: string) {
  const query = userId ? `?userId=${userId}` : "";

  return apiFetch<Conversation[]>(`/api/conversations${query}`);
}

export async function createConversation(payload: {
  participantIds: string[];
  listingId?: string;
  title?: string;
}) {
  return apiFetch<Conversation>("/api/conversations", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function fetchMessages(conversationId: string) {
  return apiFetch<Message[]>(`/api/conversations/${conversationId}/messages`);
}

export async function deleteConversation(conversationId: string) {
  return apiFetch<void>(`/api/conversations/${conversationId}`, {
    method: "DELETE",
  });
}

export async function fetchRecentMessages(userId: string, limit = 5) {
  return apiFetch<RecentMessage[]>(
    `/api/messages/recent?userId=${userId}&limit=${limit}`,
  );
}

export async function sendMessage(
  conversationId: string,
  payload: {
    senderId?: string;
    senderName: string;
    senderRole: "eleve" | "tuteur";
    body: string;
    file?: File | null;
  },
) {
  if (payload.file) {
    const formData = new FormData();

    formData.append("body", payload.body || "");
    formData.append("file", payload.file);

    return apiFetch<Message>(`/api/conversations/${conversationId}/messages`, {
      method: "POST",
      body: formData,
    });
  }

  return apiFetch<Message>(`/api/conversations/${conversationId}/messages`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export type NotificationItem = {
  _id: string;
  userId: string;
  title: string;
  body: string;
  type: string;
  event?: string;
  link?: string;
  read: boolean;
  createdAt: string;
};

export async function fetchNotifications(userId: string) {
  return apiFetch<NotificationItem[]>(`/api/notifications?userId=${userId}`);
}

export async function markNotificationRead(notificationId: string) {
  return apiFetch<NotificationItem>(
    `/api/notifications/${notificationId}/read`,
    {
      method: "PUT",
    },
  );
}

export async function clearNotifications(userId: string) {
  return apiFetch<{ deletedCount: number }>(
    `/api/notifications?userId=${userId}`,
    { method: "DELETE" },
  );
}

export async function deleteNotification(notificationId: string) {
  return apiFetch<void>(`/api/notifications/${notificationId}`, {
    method: "DELETE",
  });
}
